import { useState, useEffect } from 'react';
import { Button, Table, Row, Col, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { ToastContainer, toast } from 'react-toastify';
import Loading from '../components/Loading';
import { getItemToEdit, updateItem } from '../services/EditItemModalService';
import { getConcepts, getSubConceptsByConcept } from '../services/Concepts';

const getUserId = () => localStorage.getItem('userId') ?? 0;

function EditItemModal({ show, handleClose, itemId }) {
    let userId = getUserId();
    const [loadingModal, setLoadingModal] = useState(true);

    const [formData, setFormData] = useState({
        id: itemId,
        formattedDate: '',
        conceptId: 0,
        subconceptId: 0,
        detail: '',
        hours: 0,
        workFromHome: false,
        comments: '',
        userId
    });

    const [concepts, setConcepts] = useState([]);
    const [subconcepts, setSubconcepts] = useState([]);
    const [requiresSubconcept, setRequiresSubconcept] = useState(false);

    const isDetailEnabled = () => {
        if (!formData.conceptId) return false;
        if (requiresSubconcept) return !!formData.subconceptId;
        return true;
    };

    const handleUpdateItem = () => {
        let pendingInputs = [];

        if (!formData.conceptId) pendingInputs.push("Concepto");
        if (requiresSubconcept && !formData.subconceptId) pendingInputs.push("Subconcepto");
        if (isDetailEnabled() && !formData.detail?.trim()) pendingInputs.push("Detalle");


        if (!formData.hours) {
            pendingInputs.push("Horas");
        }

        if (!formData.comments) {
            pendingInputs.push("Comentarios");
        }

        if (pendingInputs.length == 0) {
            updateItem(formData).then((r) => {
                if (r?.result) {
                    toast.success('Solicitud actualizada correctamente');
                    handleClose();
                } else {
                    toast.error('Lo sentimos, algo salió mal.');
                }
            });
        }
        else {
            toast.warning(`Lo sentimos, debes completar los siguientes campos: ${pendingInputs.join(', ')}`)
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    useEffect(() => {
        getConcepts().then(list => {
            const actives = (list ?? []).filter(x => x.isActive);
            setConcepts(actives);
        });
    }, []);

    useEffect(() => {
        const loadSubs = async () => {
            if (!formData.conceptId) {
                setSubconcepts([]);
                setRequiresSubconcept(false);
                return;
            }
            const subs = await getSubConceptsByConcept(formData.conceptId);
            const activeSubs = (subs ?? []).filter(s => s.isActive);
            setSubconcepts(activeSubs);
            setRequiresSubconcept(activeSubs.length > 0);
        };
        loadSubs();
    }, [formData.conceptId]);

    useEffect(() => {
        if (itemId) {
            getItemToEdit(itemId).then(itemR => {
                setFormData({
                    id: itemR.id,
                    formattedDate: itemR.formattedDate,
                    conceptId: itemR.conceptId ?? 0,
                    subconceptId: itemR.subconceptId ?? 0,
                    detail: itemR.detail ?? '',
                    hours: itemR.hours,
                    workFromHome: itemR.itsWorkFromHome,
                    comments: itemR.comments ?? '',
                    userId
                })
            }).finally(() => {
                setLoadingModal(false);
            })
        }
    }, [itemId]);

    useEffect(() => {
        if (!show) return;
        setLoadingModal(true);
    }, [show, itemId]);

    const handleConceptChange = async (e) => {
        const conceptId = parseInt(e.target.value);
        setFormData(prev => ({
            ...prev,
            conceptId,
            subconceptId: 0,
            detail: '',
        }));

        if (!conceptId) {
            setSubconcepts([]);
            setRequiresSubconcept(false);
            return;
        }

        const subs = await getSubConceptsByConcept(conceptId);
        const activeSubs = (subs ?? []).filter(s => s.isActive);
        setSubconcepts(activeSubs);
        setRequiresSubconcept(activeSubs.length > 0);
    };

    const handleSubconceptChange = (e) => {
        const subconceptId = parseInt(e.target.value);
        setFormData(prev => ({ ...prev, subconceptId, detail: '' }));
    };

    return (
        <Modal size="md" show={show} onHide={handleClose} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Editar solicitud</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    loadingModal ?
                        <Loading />
                        :
                        <>
                            <Row className='mt-3'>
                                <Col >
                                    <Form.Group className='' controlId="">
                                        <Form.Label>Fecha del tiempo extra:</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="formattedDate"
                                            value={formData.formattedDate}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mt-3'>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Concepto:</Form.Label>
                                        <Form.Control as="select" value={formData.conceptId} onChange={handleConceptChange}>
                                            <option value={0}>Seleccionar concepto</option>
                                            {concepts.map(c => (
                                                <option key={c.id} value={c.id}>{c.description}</option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mt-3'>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Subconcepto:</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={formData.subconceptId}
                                            onChange={handleSubconceptChange}
                                            disabled={!requiresSubconcept}
                                        >
                                            <option value={0}>
                                                {requiresSubconcept ? "Seleccionar subconcepto" : "No aplica"}
                                            </option>
                                            {subconcepts.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mt-3'>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Detalle:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="detail"
                                            placeholder={isDetailEnabled() ? "Ingresa el detalle" : "Selecciona concepto/subconcepto"}
                                            value={formData.detail}
                                            disabled={!isDetailEnabled()}
                                            onChange={(e) => setFormData(prev => ({ ...prev, detail: e.target.value }))}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mt-3' >
                                <Col >
                                    <Form.Group className='' controlId="">
                                        <Form.Label>Horas:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="hours"
                                            value={formData.hours}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mt-3' >
                                <Col >
                                    <Form.Group className='mt-3' controlId="formWorkFromHome">
                                        <Form.Label>Trabajo desde casa:</Form.Label>
                                        <Form.Check
                                            type="radio"
                                            label="Sí"
                                            name="workFromHome"
                                            value="true"
                                            checked={formData.workFromHome === true}
                                            onChange={() => setFormData({ ...formData, workFromHome: true })}
                                        />
                                        <Form.Check
                                            type="radio"
                                            label="No"
                                            name="workFromHome"
                                            value="false"
                                            checked={formData.workFromHome === false}
                                            onChange={() => setFormData({ ...formData, workFromHome: false })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mt-3' >
                                <Col >
                                    <Form.Group className='' controlId="">
                                        <Form.Label>Comentario:</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="comments"
                                            placeholder='Ingresar razón del cambio'
                                            value={formData.comments}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row></>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" onClick={handleUpdateItem}>
                    Guardar cambios
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default EditItemModal;